/**
 * The experiment's header, in two rows: the name and status with the actions
 * its state allows (Edit on a draft, else Duplicate; Run failed items once it
 * finishes with failures), then one muted line —
 * dataset · agent version · who · when — with a "Details" toggle for where it
 * ran, run settings and evaluators. Below it: live progress, any scoring run in
 * flight, stale-evaluator and stop banners. Archived, it is read-only with a
 * banner to restore it; with an archived dataset or evaluator it can't be
 * duplicated until they are restored.
 */

import { useId, useState } from "react";
import { Link } from "react-router";
import { ChevronDown } from "lucide-react";
import { Button, ButtonLink } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { Crumbs, PageHeader } from "@/components/ui/page";
import { errorText } from "@/components/ui/states";
import { formatDateTime } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { useExperimentAction } from "../hooks";
import { RunProgress } from "./run-progress";
import { failedToRetry, RunFailedButton } from "./run-failed-button";
import { ScoringProgress, StaleBanner } from "../scoring/scoring-status";
import { stopMessage } from "../status";
import { ExperimentStatusPill } from "../status-pill";
import { ExperimentDetails } from "./experiment-details";
import { CollectingTraces, TraceRunStatus } from "./trace-run-status";
import { formatPeriod, isRuleRun, sourceErrorCode, traceChips, traceRunTitle } from "./trace-run";
import { Reconnect } from "@/features/settings/reconnect";
import { DriftBadge, versionLabel } from "./target-line";
import type { ExperimentSummary } from "../types";
import { ProjectAction } from "@/features/projects/project-action";
import { PinnedPullLine } from "../new/pulls/pinned-pull-line";
import { datasetLabel } from "../dataset-label";
import { ArchivedBadge, ArchivedBanner } from "@/features/archive/archived";
import { useRestore } from "@/features/archive/hooks";
import { isArchived } from "@/features/archive/kinds";
import { useMayArchive } from "@/features/archive/may";
import { ExperimentArchiveMenu, mayRunAgain, RestoreToRun } from "./experiment-archive";

export function ExperimentHeader({ experiment: e }: { experiment: ExperimentSummary }) {
  const action = useExperimentAction(e.id);
  const notify = useUiStore((s) => s.notify);
  const run = (a: Parameters<typeof action.mutate>[0], done: string) =>
    action.mutate(a, {
      onSuccess: () => notify(done),
      onError: (err) => notify(errorText(err, "That did not work. Try again."), "error"),
    });
  const banner = stopMessage(e);
  const [details, setDetails] = useState(false);
  const detailsId = useId();
  const version = versionLabel(e.agent_version);
  const rule = isRuleRun(e);
  const pull = !!e.pull;
  const archived = isArchived(e);
  const mayRestore = useMayArchive("experiment")(e);
  const restore = useRestore("experiment");

  return (
    <>
      <div className="flex flex-col gap-2">
        <Crumbs
          items={[
            { label: "Experiments", to: "/experiments" },
            e.agent
              ? { label: e.agent.name, to: `/agents/${e.agent.id}` }
              : { label: rule ? "Live dataset" : "Dataset outputs" },
            { label: e.name },
          ]}
        />
        <PageHeader
          title={traceRunTitle(e)}
          badges={
            <>
              <ExperimentStatusPill experiment={e} />
              <DriftBadge experiment={e} />
              {archived && <ArchivedBadge />}
            </>
          }
          subtitle={
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
              {e.dataset && (
                <Link
                  className="link"
                  to={`/datasets/${e.dataset.id}${e.dataset.version == null ? "" : `?v=${e.dataset.version}`}`}
                >
                  {datasetLabel(e.dataset)}
                </Link>
              )}
              {e.dataset?.archive && <ArchivedBadge />}
              {/* The span really pulled, once known: "Pull next" starts after the last pull, not at the period start. */}
              {pull && (
                <span>
                  {[formatPeriod(e.pull?.window ?? e.pull?.period), e.pull?.connection.name]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              )}
              {pull && <PinnedPullLine experiment={e} />}
              {!rule && version && (
                <span>
                  · agent <span className="font-mono">{version}</span>
                </span>
              )}
              <span>
                {e.dataset || pull ? "· " : ""}
                {e.created_by}
              </span>
              <span>· {formatDateTime(e.created_at)}</span>
              <span aria-hidden>·</span>
              <button
                type="button"
                className="inline-flex items-center gap-0.5 text-muted hover:text-ink"
                aria-expanded={details}
                aria-controls={detailsId}
                onClick={() => setDetails((d) => !d)}
              >
                Details
                <ChevronDown size={13} className={details ? "rotate-180" : ""} aria-hidden />
              </button>
            </span>
          }
          actions={
            <>
              {e.status === "draft" && !archived && (
                <ProjectAction permission="experiments:write">
                  <ButtonLink to={`/experiments/new?draft=${e.id}`}>Edit</ButtonLink>
                </ProjectAction>
              )}
              {e.status === "draft" && !archived && (
                <ProjectAction permission="experiments:write">
                  <Button
                    variant="primary"
                    disabled={action.isPending}
                    onClick={() => run("start", "Experiment started.")}
                  >
                    Run experiment
                  </Button>
                </ProjectAction>
              )}
              {!archived && <RunFailedButton experiment={e} />}
              {e.status !== "draft" && !archived && (
                <ProjectAction permission="experiments:write">
                  {mayRunAgain(e) ? (
                    <ButtonLink to={`/experiments/new?from=${e.id}`}>Duplicate</ButtonLink>
                  ) : (
                    <Button disabled title="Restore to run again.">
                      Duplicate
                    </Button>
                  )}
                </ProjectAction>
              )}
              {!archived && <ExperimentArchiveMenu experiment={e} />}
            </>
          }
        />
        {details && <ExperimentDetails id={detailsId} experiment={e} />}
      </div>
      <ArchivedBanner kind="experiment" row={e} mayRestore={mayRestore} onRestore={() => void restore(e)}>
        {failedToRetry(e) && "Restore it to run the failed items again."}
      </ArchivedBanner>
      {!archived && e.status !== "draft" && <RestoreToRun experiment={e} />}

      {rule && e.status !== "collecting" && traceChips(e).length > 0 && (
        <ul aria-label="Trace counts" className="flex flex-wrap gap-1.5">
          {traceChips(e).map((chip) => (
            <li key={chip} className="max-w-full">
              {/* A fetch chip can name trace ids: let it wrap on a phone. */}
              <Pill className="h-auto min-h-5 py-0.5 whitespace-normal">{chip}</Pill>
            </li>
          ))}
        </ul>
      )}
      {rule && <TraceRunStatus experiment={e} />}
      {!rule && e.status === "collecting" && <CollectingTraces experiment={e} />}
      {!rule && e.status !== "collecting" && <RunProgress experiment={e} />}

      {!rule && <ScoringProgress experiment={e} />}
      <StaleBanner experiment={e} />

      {banner && (
        <section
          role="alert"
          className={`rounded-[9px] px-4 py-3.5 text-[13px] leading-normal ${e.status === "failed" ? "bg-err-bg text-err" : "bg-warn-bg text-warn"}`}
        >
          {banner}
          {e.pull && sourceErrorCode(e) === "auth" && (
            <span className="mt-2 flex">
              <Reconnect
                connectionId={e.pull.connection.id}
                connectorType={e.pull.connection.connector_type}
              />
            </span>
          )}
        </section>
      )}
    </>
  );
}
